"use client";

import { useTranslations } from "next-intl";
import { Chip, HStack, Stack, Text } from "@leabankab/component-library";
import { useConstantSelectOptions } from "@/features/customer-information/hooks/use-constant-options";
import type { LockedDebt } from "@/features/refinance/utils/lea-bank-debts";
import { useCurrency } from "@/hooks";

const CAPTION_KEYS = {
	topUp: "lockedDebt.topUpCaption",
	notRefinanceable: "lockedDebt.notRefinanceableCaption",
	existingLoan: "lockedDebt.existingLoanCaption",
} as const;

/**
 * A Lea Bank debt shown for completeness but never selectable. The caption, not
 * the grey, is what tells the customer why.
 */
export const LockedDebtCard = ({ amount, description, reason }: LockedDebt) => {
	const t = useTranslations("pages.RefinancePage");
	const { format } = useCurrency();
	const typeOptions = useConstantSelectOptions("loan-types");

	const typeLabel = typeOptions.find(
		(option) => option.value === description
	)?.label;

	return (
		<Stack
			gap={0.5}
			className="rounded-xl border border-border-subtle bg-[color-mix(in_srgb,var(--lea-color-neutral-50)_50%,white)] px-2 py-1.5"
		>
			<HStack align="center" gap={1}>
				<Text fontWeight="medium" color="text.base">
					{t("lockedDebt.creditGiver")}
				</Text>
				{typeLabel && (
					<Chip label={typeLabel} size="sm" variant="filled" color="neutral" />
				)}
			</HStack>
			<Text color="text.base">{format(amount)}</Text>
			<Text textStyle="body-sm" color="text.base">
				{t(CAPTION_KEYS[reason])}
			</Text>
		</Stack>
	);
};
